import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { useQuerystring } from "$routes/_lib/use-querystring";
import { gql } from "@apollo/client";
import {
  type QueryPullReferralFeedbackArgs,
  type ReferralFeedbackSyncPushInput,
  ReferralFeedbackType,
} from "elephant-web/gql";
import { useMemo } from "react";
import { useMatch } from "react-router-dom";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { ReferralFeedback } from "../collections/referral-feedback";
import type { RxDBCollections } from "../provider";
import {
  SyncReferralFeedbackPullDocument,
  SyncReferralFeedbackPushDocument,
  type WholeSyncableReferralFeedbackFragment,
} from "./__generated__/referral-feedback.gql";
import { createPersistedCheckpointPullBuilders } from "./create-persisted-checkpoint-pull-builders";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicateReferralFeedback = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<ReferralFeedback>;
}) => {
  const query = useQuerystring();
  const matchPat = useMatch("/dev-patient/:patientId/*");
  const patientId = matchPat?.params.patientId ?? query.patientUuid;
  const [featureFlagEnabled] = useFeatureFlag(featureFlags.pocus.referralsSync);

  const push = useMemo(
    () => ({
      mutation: SyncReferralFeedbackPushDocument,
      mapRxToGql: mapRxReferralFeedbackToGqlReferralFeedbackInput,
    }),
    [],
  );

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncableReferralFeedbackFragment> | undefined
  >(() => {
    return featureFlagEnabled && patientId
      ? {
          query: SyncReferralFeedbackPullDocument,
          reSyncInterval: { minutes: 5 },
          ...createPersistedCheckpointPullBuilders<
            QueryPullReferralFeedbackArgs,
            WholeSyncableReferralFeedbackFragment
          >({
            buildInputVariables: (checkpoint, batchSize) => ({
              input: {
                limit: batchSize,
                filters: { patientId },
                checkpoint,
              },
            }),
            buildPullCheckpoint: (doc) => ({
              id: doc.id,
              updatedAt: doc.meta.updatedAt,
            }),
            entityId: patientId,
            db,
            collectionName: collection?.name,
          }),
        }
      : undefined;
  }, [featureFlagEnabled, patientId, db, collection]);

  return useGqlReplication<
    ReferralFeedback,
    WholeSyncableReferralFeedbackFragment,
    ReferralFeedbackSyncPushInput
  >({
    db,
    collection,
    push: featureFlagEnabled && patientId ? push : undefined,
    pull,
    mapGqlToRx: mapGqlReferralFeedbackToRxReferralFeedback,
    batchSize: 100,
    expireData: true,
    metadataLocalStorageKeySuffix: `referral-feedback-${patientId}`,
    enabled: featureFlagEnabled && !!patientId,
  });
};

export const mapGqlReferralFeedbackToRxReferralFeedback = (
  referralFeedback: WholeSyncableReferralFeedbackFragment,
): WithDeleted<ReferralFeedback> => ({
  id: referralFeedback.id,
  referralId: referralFeedback.referralId,
  feedbackType: referralFeedback.feedbackType,
  patientId: referralFeedback.patientId,
  meta: {
    createdAt: referralFeedback.meta.createdAt,
    updatedAt: referralFeedback.meta.updatedAt,
    createdByUserId: referralFeedback.meta.createdByUserId,
    updatedByUserId: referralFeedback.meta.updatedByUserId,
  },
  _deleted: referralFeedback.meta.deleted,
});

export const mapRxReferralFeedbackToGqlReferralFeedbackInput = (
  referralFeedback: WithDeleted<ReferralFeedback>,
): ReferralFeedbackSyncPushInput => ({
  id: referralFeedback.id,
  referralId: referralFeedback.referralId,
  feedbackType: referralFeedback.feedbackType as ReferralFeedbackType,
  patientId: referralFeedback.patientId,
  meta: {
    createdAt: referralFeedback.meta.createdAt,
    updatedAt: referralFeedback.meta.updatedAt,
    createdByUserId: referralFeedback.meta.createdByUserId,
    updatedByUserId: referralFeedback.meta.updatedByUserId,
    deleted: referralFeedback._deleted,
  },
});

gql`
  fragment WholeSyncableReferralFeedback on SyncableReferralFeedback {
    id
    referralId
    feedbackType
    patientId
    meta {
      createdAt
      updatedAt
      createdByUserId
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncReferralFeedbackPull($input: PullReferralFeedbackInput!) {
    pullReferralFeedback(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableReferralFeedback
      }
    }
  }
`;

gql`
  mutation SyncReferralFeedbackPush($input: PushReferralFeedbackInput!) {
    pushReferralFeedback(input: $input) {
      rows {
        ...WholeSyncableReferralFeedback
      }
    }
  }
`;
